import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Sidebar } from '../components/sidebar';
import { ChatSessionLayout } from '../components/chat-session-layout';
import { chatService } from '../services/chatService';
import { vehicleService } from '../services/vehicleService';
import { Chat } from '../types/chat';
import { SourcesManager, Source } from '../lib/sources-utils';
import { useMobile } from '../hooks/use-mobile';

/**
 * Public read-only view of a shared chat.
 * No input box, no preview banner, only messages and sources.
 */
const ViewChatPage: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const [chat, setChat] = useState<Chat | null>(null);
  const [vehicleName, setVehicleName] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [highlightedSourceId, setHighlightedSourceId] = useState<string | undefined>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sourcesManagerRef = useRef(new SourcesManager());
  const [sources, setSources] = useState<Source[]>([]);
  const isMobile = useMobile();
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isMobile) {
      setShowSidebar(false);
      setShowSources(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const fetchPublicChat = async () => {
      if (!chatId) return;
      setLoading(true);
      try {
        const publicChat = await chatService.getPublicChat(chatId);
        if (!publicChat) {
          setNotFound(true);
          return;
        }
        if (!Array.isArray(publicChat.messages)) (publicChat as any).messages = [];
        setChat({ ...publicChat });

        // Parse assistant messages for sources
        sourcesManagerRef.current.clearSources();
        publicChat.messages.forEach(msg => {
          if (msg.role === 'assistant') {
            sourcesManagerRef.current.parseAndReplaceLinks(msg.content);
          }
        });
        setSources(sourcesManagerRef.current.getAllSources());

        // Fetch vehicle name (no token needed for constants fallback)
        try {
          const name = await vehicleService.getVehicleNameById(publicChat.vehicleId);
          setVehicleName(name);
        } catch (e) {
          setVehicleName('');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPublicChat();
  }, [chatId]);

  if (notFound) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-zinc-900 to-black text-white p-4 text-center">
        <div>
          <h1 className="text-2xl font-bold mb-2">Chat Not Found</h1>
          <p className="text-zinc-400">This chat either does not exist or is not public.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-zinc-900 to-black text-white overflow-hidden mobile-viewport safe-area-bottom">
      {/* Sidebar in view mode shows only marketing content (preview mode) */}
      <Sidebar
        chats={[]}
        currentChatId={chatId || ''}
        onSelectChat={() => {}}
        onCreateNewChat={() => {}}
        onDeleteChat={() => {}}
        onRenameChat={() => {}}
        showSidebar={showSidebar}
        onToggleSidebar={() => setShowSidebar(!showSidebar)}
        onAddVehicle={() => {}}
        isPreviewMode={true}
        previewVehicleName={vehicleName}
      />
      <ChatSessionLayout
        chatName={chat?.title || (loading ? 'Loading Chat...' : 'Shared Chat')}
        vehicleName={vehicleName}
        showSidebar={showSidebar}
        onToggleSidebar={() => setShowSidebar(!showSidebar)}
        showSources={showSources}
        onToggleSources={() => setShowSources(!showSources)}
        sources={sources}
        highlightedSourceId={highlightedSourceId}
        messages={chat?.messages || []}
        streamedMessage={undefined}
        isLoading={loading}
        messagesEndRef={messagesEndRef}
        chatId={chat?.id}
        onSourceClick={(source) => {
          setHighlightedSourceId(source.id);
          setShowSources(true);
          setTimeout(() => setHighlightedSourceId(undefined), 3000);
        }}
      />
    </div>
  );
};

export default ViewChatPage;
